import { useMutation, useQuery, useSubscription } from '@apollo/client';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  i18n,
  mediaQuery,
  openCallMeModal,
  setCustomer,
  setFlowType,
  setSoId,
  setSoIdWeb,
  showMessage,
  useAppDispatch
} from 'src/common';
import { GetOfferDto, getOffersAdditional, GetOffersBySoIdQuery, GetOffersBySoIdQueryVariables, getSoAdditional, GetSoQuery, GetSoQueryVariables, OFFERS_CHANGED_SUBSCRIPTION, selectOfferAdditionalMutation, SelectOfferMutation, SelectOfferMutationVariables } from 'src/common/api/apollo/additional';
import { Flex, Loader, OfferAdditionalCard, RequestAgentCardV2, Text } from 'src/components';
import { baseWidths, colors } from 'src/theme';
import { AppRoutesAdditional } from '../../_router/app.routes';

const TitleStyle = styled(Flex)({
  backgroundColor: colors.white,
  padding: '8px 20px',
  paddingTop: 32,
  flexDirection: 'row',
  maxWidth: '100vw',

  [mediaQuery('desktop')]: {
    paddingTop: 12,
  },
});

const TitleCenter = styled(Flex)({
  width: `${baseWidths.mobile}px`,
  maxWidth: '100%',
  margin: '0 auto',
  boxSizing: 'border-box',
});

const AgentAvatar = styled.img({
  width: 32,
  height: 32,
  borderRadius: '50%',
  backgroundColor: colors.primary4,
  flexShrink: 0,
});

const CallAgentLink = styled.button({
  display: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  font: 'inherit',
  color: colors.primary,
  cursor: 'pointer',
  textAlign: 'left',
  fontSize: 14,
  fontWeight: 700,
  '&:hover': { opacity: 0.8 },

  [mediaQuery('desktop')]: {
    display: 'block',
  },
});

const CallAgentLinkMobile = styled.button({
  display: 'block',
  background: 'none',
  border: 'none',
  padding: 0,
  font: 'inherit',
  fontWeight: 700,
  color: colors.primary,
  cursor: 'pointer',
  [mediaQuery('desktop')]: {
    display: 'none',
  },
});

const AgentInfoStyle = styled.div({
  display: 'flex',
  width: '100%',
  flexDirection: 'row',
  gap: 16,
  alignItems: 'center',
  [mediaQuery('desktop')]: {
    position: 'absolute',
    right: 100,
    width: 300,
    display: 'flex',
    gap: 16,
    alignItems: 'center',
  },

  [mediaQuery('desktopL')]: {
    right: 240,
  }
});

const OffersStyle = styled(Flex)({
  margin: '0 auto',
  maxWidth: '100%',
  boxSizing: 'border-box',
  [mediaQuery('mobile')]: {
    width: `${baseWidths.mobile}px`,
    paddingLeft: 12,
    paddingRight: 12,
  },

  [mediaQuery('desktop')]: {
    paddingLeft: 0,
    paddingRight: 0,
  },
});

const CustomerInfoStyleDesktop = styled.div({
  backgroundColor: colors.white,
  padding: '8px 20px',
  paddingTop: 12,
  flexDirection: 'row',
  borderRadius: 4,
  position: 'absolute',
  right: 100,
  width: 320,
  top: 170,
  display: 'none',

  '&::before': {
    content: '""',
    position: 'absolute',
    left: 24,
    top: -8,
    width: 0,
    height: 0,
    borderLeft: '8px solid transparent',
    borderRight: '8px solid transparent',
    borderBottom: `8px solid ${colors.white}`,
  },

  [mediaQuery('desktop')]: {
    display: 'block',
  },

  [mediaQuery('desktopL')]: {
    right: 240,
  },
});

const CustomerInfoStyleMobile = styled.div({
  position: 'relative',
  display: 'block',
  backgroundColor: colors.white,
  paddingTop: 12,
  paddingBottom: 12,
  flexDirection: 'row',
  borderRadius: 4,

  '&::before': {
    content: '""',
    position: 'absolute',
    left: 8,
    top: 4,
    width: 0,
    height: 0,
    borderLeft: '8px solid transparent',
    borderRight: '8px solid transparent',
    borderBottom: `8px solid ${colors.background6}`,
  },

  [mediaQuery('desktop')]: {
    display: 'none',
  },
});

const CustomerInfoStyleMobileText = styled(Text)({
  backgroundColor: colors.background6,
  padding: '8px 20px',
  borderRadius: 4,
});

const AgentOffersPreviewPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { soId } = useParams();

  const { data: getSoAdditionalData, loading: loadingSoAdditional } = useQuery<GetSoQuery, GetSoQueryVariables>(getSoAdditional, {
    variables: { id: soId || '' },
    skip: !soId,
    fetchPolicy: 'network-only',
    onError: () => {
      navigate(AppRoutesAdditional.NO_OFFER.fullPath);
    }
  });

  const { data: getOffersAdditionalData, loading: loadingOffersAdditional, refetch: refetchOffers } = useQuery<GetOffersBySoIdQuery, GetOffersBySoIdQueryVariables>(getOffersAdditional, {
    variables: { id: soId || '' },
    skip: !soId,
    fetchPolicy: 'network-only',
    onError: () => {
      navigate(AppRoutesAdditional.NO_OFFER.fullPath);
    }
  });

  const [selectOfferAdditional] = useMutation<SelectOfferMutation, SelectOfferMutationVariables>(selectOfferAdditionalMutation);

  useSubscription(OFFERS_CHANGED_SUBSCRIPTION, {
    variables: { soId: soId || '' },
    skip: !soId,
    onData: () => {
      refetchOffers();
    },
  });

  useEffect(() => {
    dispatch(setSoIdWeb(soId));
    dispatch(setSoId(soId));
    dispatch(setFlowType('agent'));
  }, [dispatch, soId]);

  useEffect(() => {
    if (getSoAdditionalData?.getSo?.productTypeSpecific?.insuredPerson) {
      dispatch(setCustomer({
        birthDate: getSoAdditionalData?.getSo?.productTypeSpecific?.insuredPerson?.birthDate ?? '',
        age: getSoAdditionalData?.getSo?.productTypeSpecific?.insuredPerson?.birthDate ? dayjs().diff(dayjs(getSoAdditionalData?.getSo?.productTypeSpecific?.insuredPerson?.birthDate), 'year') : undefined,
        firstName: getSoAdditionalData?.getSo?.productTypeSpecific?.insuredPerson?.firstName ?? '',
        lastName: getSoAdditionalData?.getSo?.productTypeSpecific?.insuredPerson?.lastName ?? '',
      }));
    }
  }, [getSoAdditionalData?.getSo?.productTypeSpecific?.insuredPerson, dispatch]);

  const navigateToNoOffer = () => {
    navigate(AppRoutesAdditional.NO_OFFER.fullPath);
  };

  useEffect(() => {
    if (!soId) {
      navigateToNoOffer();
    }
  }, [navigate, soId]);

  if (loadingSoAdditional || loadingOffersAdditional) return <Loader page />;

  const sortedOffers = getOffersAdditionalData?.getOffersBySoId ?
    getOffersAdditionalData.getOffersBySoId.slice().sort((a, b) =>
      (a.productTypeSpecific?.calculatedPrices?.total?.generalTransfer ?? 0) - (b.productTypeSpecific?.calculatedPrices?.total?.generalTransfer ?? 0)
    ) : []

  const handleSelectOffer = (offer: GetOfferDto) => {
    selectOfferAdditional({ variables: { id: offer._id } })
      .then((response) => {
        if (offer.product?.brand?.code === 'agramLife' || offer.productTypeSpecific?.healthQuestionnaire?.completedAt) {
          navigate(`${AppRoutesAdditional.OFFER.fullPath}/${response.data?.selectOffer}`);
        } else {
          navigate(`${AppRoutesAdditional.HEALTH_QUESTIONNAIRE.fullPath}/${response.data?.selectOffer}`);
        }
      })
      .catch(() => {
        dispatch(showMessage({
          message: i18n.t('common.globalError'),
          type: 'error',
          duration: 10000,
        })
        );
      });
  };

  return (
    <Flex vertical gap={24} style={{ marginTop: -40, overflowX: 'hidden', maxWidth: '100%' }}>
      <TitleStyle align="center" justify="space-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        {getSoAdditionalData?.getSo?.admin && (
          <AgentInfoStyle>
            <AgentAvatar src={getSoAdditionalData?.getSo?.admin?.avatar ?? ''} />
            <Flex vertical>
              <Text color={colors.text1} type="small" >{getSoAdditionalData?.getSo?.admin?.name ?? i18n.t('pages.agentOffersPreviewPage.agentName')}</Text>
              <CallAgentLink type="button" onClick={() => dispatch(openCallMeModal())}>
                {i18n.t('pages.agentOffersPreviewPage.callAgent')}
              </CallAgentLink>
            </Flex>
            <Flex justify="flex-end" style={{ flex: 1 }}>
              <CallAgentLinkMobile type="button" onClick={() => dispatch(openCallMeModal())}>
                {i18n.t('pages.agentOffersPreviewPage.callAgent')}
              </CallAgentLinkMobile>
            </Flex>
          </AgentInfoStyle>
        )}
        <TitleCenter>
          <Text type="sLarge" bold>
            {i18n.t('pages.agentOffersPreviewPage.title')}
          </Text>
        </TitleCenter>
        <CustomerInfoStyleMobile>
          <CustomerInfoStyleMobileText type="base" color={colors.black} dangerouslySetInnerHTML={{
            __html: i18n.t('pages.agentOffersPreviewPage.customerInfo',
              { name: `${getSoAdditionalData?.getSo?.productTypeSpecific?.insuredPerson?.firstName ?? ""} ${getSoAdditionalData?.getSo?.productTypeSpecific?.insuredPerson?.lastName ?? ""}`, dateOfBirth: getSoAdditionalData?.getSo?.productTypeSpecific?.insuredPerson?.birthDate ?? "" })
          }} >
          </CustomerInfoStyleMobileText>
        </CustomerInfoStyleMobile>
      </TitleStyle>
      <CustomerInfoStyleDesktop>
        <Text type="base" color={colors.black} dangerouslySetInnerHTML={{
          __html: i18n.t('pages.agentOffersPreviewPage.customerInfo',
            { name: `${getSoAdditionalData?.getSo?.productTypeSpecific?.insuredPerson?.firstName ?? ""} ${getSoAdditionalData?.getSo?.productTypeSpecific?.insuredPerson?.lastName ?? ""}`, dateOfBirth: getSoAdditionalData?.getSo?.productTypeSpecific?.insuredPerson?.birthDate ?? "" })
        }} >
        </Text>
      </CustomerInfoStyleDesktop>
      <OffersStyle vertical gap={24}>
        {sortedOffers.map((offer: any) => {
          const premiumEligibleAgeMin = offer.product?.premiumEligibleAgeMin;
          const premiumEligibleAgeMax = offer.product?.premiumEligibleAgeMax;
          const offerSubDescription =
            typeof premiumEligibleAgeMax === 'number' && premiumEligibleAgeMax > 100
              ? i18n.t('pages.offersAdditionalPage.offerSubDescriptionFromAge', { premiumEligibleAgeMin })
              : i18n.t('pages.offersAdditionalPage.offerSubDescription', { premiumEligibleAgeMin, premiumEligibleAgeMax });

          return (
            <OfferAdditionalCard
              key={offer._id}
              offer={offer}
              showSelectButton
              onSelectOffer={handleSelectOffer}
              selectButtonText={i18n.t('pages.offersAdditionalPage.selectButton')}
              offerSubDescription={offerSubDescription}
            />
          );
        })}
      </OffersStyle>
      <Flex justify="center" style={{ marginLeft: 12, marginRight: 12 }}>
        <RequestAgentCardV2 onButtonClick={() => dispatch(openCallMeModal())} />
      </Flex>
    </Flex>
  );
};

export default AgentOffersPreviewPage;
